import React, { createContext, useContext, useState } from 'react';
import type { AuthRepository } from '../../application/ports/AuthRepository';
import type { DeviceRepository } from '../../application/ports/DeviceRepository';
import type { SiteRepository } from '../../application/ports/SiteRepository';
import type { HubRepository } from '../../application/ports/HubRepository';
import type { LotRepository } from '../../application/ports/LotRepository';
import type { DeviceShareRepository } from '../../application/ports/DeviceShareRepository';
import type { AlertRepository } from '../../application/ports/AlertRepository';
import type { AlertEventRepository } from '../../application/ports/AlertEventRepository';
import type { MeasurementRepository } from '../../application/ports/MeasurementRepository';
import type { PushRepository } from '../../application/ports/PushRepository';
import type { AdminRepository } from '../../application/ports/AdminRepository';
import type { WeatherRepository } from '../../application/ports/WeatherRepository';
import type { AiRepository } from '../../application/ports/AiRepository';
import type { IrrigationRepository } from '../../application/ports/IrrigationRepository';
import type { WeatherStationRepository } from '../../application/ports/WeatherStationRepository';

import { HttpAuthRepository } from './HttpAuthRepository';
import { HttpDeviceRepository } from './HttpDeviceRepository';
import { HttpSiteRepository } from './HttpSiteRepository';
import { HttpHubRepository } from './HttpHubRepository';
import { HttpLotRepository } from './HttpLotRepository';
import { HttpDeviceShareRepository } from './HttpDeviceShareRepository';
import { HttpAlertRepository } from './HttpAlertRepository';
import { HttpAlertEventRepository } from './HttpAlertEventRepository';
import { HttpMeasurementRepository } from './HttpMeasurementRepository';
import { HttpPushRepository } from './HttpPushRepository';
import { HttpAdminRepository } from './HttpAdminRepository';
import { HttpWeatherRepository } from './HttpWeatherRepository';
import { HttpAiRepository } from './HttpAiRepository';
import { HttpIrrigationRepository } from './HttpIrrigationRepository';
import { HttpWeatherStationRepository } from './HttpWeatherStationRepository';

export interface Repositories {
  auth: AuthRepository;
  devices: DeviceRepository;
  sites: SiteRepository;
  hubs: HubRepository;
  lots: LotRepository;
  deviceShares: DeviceShareRepository;
  alerts: AlertRepository;
  alertEvents: AlertEventRepository;
  measurements: MeasurementRepository;
  push: PushRepository;
  admin: AdminRepository;
  weather: WeatherRepository;
  ai: AiRepository;
  irrigation: IrrigationRepository;
  weatherStations: WeatherStationRepository;
}

function createHttpRepositories(): Repositories {
  return {
    auth: new HttpAuthRepository(),
    devices: new HttpDeviceRepository(),
    sites: new HttpSiteRepository(),
    hubs: new HttpHubRepository(),
    lots: new HttpLotRepository(),
    deviceShares: new HttpDeviceShareRepository(),
    alerts: new HttpAlertRepository(),
    alertEvents: new HttpAlertEventRepository(),
    measurements: new HttpMeasurementRepository(),
    push: new HttpPushRepository(),
    admin: new HttpAdminRepository(),
    weather: new HttpWeatherRepository(),
    ai: new HttpAiRepository(),
    irrigation: new HttpIrrigationRepository(),
    weatherStations: new HttpWeatherStationRepository(),
  };
}

const RepositoryContext = createContext<Repositories | undefined>(undefined);

// DI del frontend: los casos de uso (hooks) y páginas piden los repositorios
// por acá en vez de importar los Http*Repository directo — así se puede
// inyectar una implementación fake en tests sin tocar ni un componente.
export const RepositoryProvider: React.FC<{ children: React.ReactNode; repositories?: Repositories }> = ({
  children,
  repositories,
}) => {
  const [instance] = useState(() => repositories ?? createHttpRepositories());
  return <RepositoryContext.Provider value={instance}>{children}</RepositoryContext.Provider>;
};

export function useRepositories(): Repositories {
  const ctx = useContext(RepositoryContext);
  if (!ctx) throw new Error('useRepositories debe usarse dentro de un RepositoryProvider');
  return ctx;
}
